import { fromJS, Map } from 'immutable';
import { RestAPI } from '@/util/net.js';
import { putError } from '@/store/error.store';
import momentLocalizer from 'react-widgets-moment';
import moment from 'moment';
import { userHasReportPermissions,
  validateRoleConfig, ReportName } from '../../../auth/RoleConfigFilter';

moment.locale('en');
momentLocalizer();

const toDate = (momentDate) => momentDate.startOf('month').toDate();
const utcDate = (date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(),
  date.getDate(), date.getHours(), date.getMinutes()));
const localDateToString = d => moment(`${d.dayOfMonth}.${d.monthValue}.${d.year}`, 'DD.MM.YYYY');

const minDate = (date) => {
  if (date) {
    return utcDate(toDate(localDateToString(date)));
  }
  return utcDate(toDate(moment()));
};

const updateFieldByIdxSD = (state, idx, updateFunc) =>
  state.set('data', state.get('data')
    .map((d) => {
      if (d.get('id') === idx) {
        return updateFunc(d);
      }
      return d;
    }));

const updateAllFieldsSD = (state, updateFunc) =>
  state.set('data', state.get('data')
    .map((d) => updateFunc(d)));

const updateCheckStateSD = (state, idx, checkState) =>
  updateFieldByIdxSD(state, idx, d => d.set('checkState', checkState));

const updateAllCheckStateSD = (state, checkState) =>
  updateAllFieldsSD(state, d => d.set('checkState', checkState));

export const supplyDemandReducer = (state = new Map(), action) => {
  switch (action.type) {
    case 'SUPPLY_DEMAND_REPORT_NEW_RECORD':
      return state.set('data', state.get('data').push(new Map().set('idx', state.size)
        .set('data', fromJS([
          { date: utcDate(toDate(moment())) },
          { date: utcDate(toDate(moment().add(1, 'month'))) },
          { date: utcDate(toDate(moment().add(2, 'month'))) },
        ]))));
    case 'SUPPLY_DEMAND_REPORT_DATA_LOADED':
      return state.set('data', fromJS(action.data));
    case 'SUPPLY_DEMAND_REPORT_OBJECT_PERSISTED':
      return state.setIn(['data', state.get('data').size - 1], fromJS(action.data));
    case 'SUPPLY_DEMAND_REPORT_OBJECT_UPDATED':
      return state.set('data', state.get('data').map(d => {
        if (d.get('id') === action.data.id) {
          return fromJS({ ...d.toJS(), ...action.data });
        }
        return d;
      }));
    case 'SUPPLY_DEMAND_REPORT_DELETED':
      return state.set('data', state.get('data').filter(d => d.get('id') !== action.id));
    case 'SUPPLY_DEMAND_REPORT_MIN_DATE_FOUND':
      return state.set('mindate', minDate(action.date));
    case 'SUPPLY_DEMAND_REPORT_CAN_USER_ACCESS_COMPLETED':
      return state.set('canAccess', action.result);
    case 'SUPPLY_DEMAND_UPDATE_CHECK_STATE':
      return updateCheckStateSD(state, action.idx, action.checkState);
    case 'SUPPLY_DEMAND_UPDATE_ALL_CHECK_STATE':
      return updateAllCheckStateSD(state, action.checkState);
    case 'SUPPLY_DEMAND_REPORT_GRAND_TOTAL_LOADED':
      return state.set('grandTotal', fromJS(action.data));
    case 'SUPPLY_DEMAND_ALL_TOTALS_LOADED':
      return state.set('totals', fromJS(action.data));
    case 'SUPPLY_DEMAND_TOTAL_NAME_UPDATED':
      return state.set('totals', state.get('totals').map(d => {
        if (d.get('id') === action.id) {
          return fromJS({ ...d.toJS(), name: action.value });
        }
        return d;
      }),
      );
    case 'SUPPLY_DEMAND_TOTAL_DELETED':
      return state.set('totals', fromJS(action.data));
    case 'SUPPLY_DEMAND_NEW_TOTAL_CREATED':
      return state.set('totals', state.get('totals', new Map()).push(fromJS(action.data)));
    case 'SUPPLY_DEMAND_TOTAL_INDEX_UPDATED':
      return state.set('totals', fromJS(action.data));
    default:
      return state;
  }
};

export const create = (dispatch, formData, created, dataLackFunc) => {
  dispatch({ type: 'SUPPLY_DEMAND_REPORT_CREATE_STARTED' });
  return RestAPI.post('/supplydemand/api/create', formData)
    .then((data) => {
      dispatch({ type: 'SUPPLY_DEMAND_REPORT_CREATED' });
      created(data);
      return true;
    }).catch((error) => {
      // eslint-disable-next-line
      if (error.exception === 'ru.sggr.starter.deal.externaldata.supplydemand.KeyFieldsNotFilledException') {
        dataLackFunc();
      } else {
        dispatch({ type: 'SUPPLY_DEMAND_REPORT_CREATE_FAILED' });
        putError(dispatch, error);
      }
    });
};

export const updateValue = (dispatch, id, data) => {
  dispatch({ type: 'SUPPLY_DEMAND_REPORT_UPDATE_VALUE_STARTED' });
  return RestAPI.patch(`/supplydemand/api/update/${id}`, data)
    .then(() => {
      dispatch({ type: 'SUPPLY_DEMAND_REPORT_VALUE_UPDATED' });
      return true;
    }).catch((error) => {
      dispatch({ type: 'SUPPLY_DEMAND_REPORT_VALUE_UPDATE_FAILED' });
      putError(dispatch, error);
    });
};

export const updateProperty = (dispatch, formData, updated, failed) => {
  dispatch({ type: 'SUPPLY_DEMAND_REPORT_UPDATE_PROPERTY_STARTED' });
  return RestAPI.patch('/supplydemand/api/update', formData)
    .then(() => {
      dispatch({ type: 'SUPPLY_DEMAND_REPORT_PROPERTY_UPDATED' });
      updated(formData);
      return true;
    }).catch((error) => {
      // eslint-disable-next-line
      if (error.exception === 'ru.sggr.starter.deal.externaldata.supplydemand.KeyFieldsNotFilledException') {
        failed(error);
      } else {
        dispatch({ type: 'SUPPLY_DEMAND_REPORT_PROPERTY_UPDATE_FAILED' });
        putError(dispatch, error);
      }
    });
};

export const updateData = (dispatch, object) => {
  if (object.id == null) {
    create(dispatch, object, (data) => {
      dispatch({ type: 'SUPPLY_DEMAND_REPORT_OBJECT_PERSISTED', idx: object.idx, data });
    }, () => {
      dispatch({ type: 'SUPPLY_DEMAND_REPORT_OBJECT_UPDATED', idx: object.idx, data: object });
    });
  } else {
    updateProperty(dispatch, object, (data) => {
      dispatch({ type: 'SUPPLY_DEMAND_REPORT_OBJECT_UPDATED', data });
    }, () => {});
  }
};

export const updateMonthData = (dispatch, id, date, value) => {
  updateValue(dispatch, id, {
    date: utcDate(date), value,
  });
};

export const newRecord = (dispatch) => {
  dispatch({ type: 'SUPPLY_DEMAND_REPORT_NEW_RECORD' });
};

export const loadData = (dispatch) => {
  dispatch({ type: 'SUPPLY_DEMAND_REPORT_DATA_LOADING' });
  let url = '/supplydemand/api/responsible';
  if (userHasReportPermissions([ReportName.SUPPLY_DEMAND])
    || validateRoleConfig(['management', 'viewReports'])) {
    url = '/supplydemand/api/all';
  }
  return RestAPI.get(url)
    .then(
      (data) => {
        dispatch({ type: 'SUPPLY_DEMAND_REPORT_DATA_LOADED', data });
        return true;
      },
    ).catch(
      (data) => {
        putError(dispatch, data);
        dispatch({ type: 'SUPPLY_DEMAND_REPORT_DATA_LOADING_FAILED' });
        return true;
      },
    );
};

export const deleteRecord = (dispatch, id) => {
  if (id == null) {
    dispatch({ type: 'SUPPLY_DEMAND_REPORT_DELETED', id });
  } else {
    dispatch({ type: 'SUPPLY_DEMAND_REPORT_DELETE_STARTED' });
    RestAPI.delete(`/supplydemand/api/delete/${id}`)
      .then(
        () => {
          dispatch({ type: 'SUPPLY_DEMAND_REPORT_DELETED', id });
          return true;
        },
      ).catch(
        (data) => {
          putError(dispatch, data);
          dispatch({ type: 'SUPPLY_DEMAND_REPORT_DELETE_FAILED' });
          return true;
        },
      );
  }
};

export const findMinDate = (dispatch) => {
  dispatch({ type: 'SUPPLY_DEMAND_REPORT_FIND_MIN_DATE' });
  return RestAPI.get('/supplydemand/api/mindate')
    .then(
      (date) => {
        dispatch({ type: 'SUPPLY_DEMAND_REPORT_MIN_DATE_FOUND', date });
        return true;
      },
    ).catch(
      (data) => {
        putError(dispatch, data);
        dispatch({ type: 'SUPPLY_DEMAND_REPORT_MIN_DATE_FAILED' });
        return true;
      },
    );
};

export const canAccess = (dispatch) => {
  dispatch({ type: 'SUPPLY_DEMAND_REPORT_CAN_USER_ACCESS_STARTING' });
  return RestAPI.get('/supplydemand/api/canaccess')
    .then(
      (result) => {
        dispatch({ type: 'SUPPLY_DEMAND_REPORT_CAN_USER_ACCESS_COMPLETED', result });
        return true;
      },
    ).catch(
      (data) => {
        putError(dispatch, data);
        dispatch({ type: 'SUPPLY_DEMAND_REPORT_CAN_USER_ACCESS_FAILED' });
        return true;
      },
    );
};

export const updateCheckStateSDDispatch = (dispatch, idx, checkState) =>
  dispatch({ type: 'SUPPLY_DEMAND_UPDATE_CHECK_STATE', idx, checkState });

export const allCheckStateSDDispatch = (dispatch, checkState) =>
  dispatch({ type: 'SUPPLY_DEMAND_UPDATE_ALL_CHECK_STATE', checkState });

export const loadGrandTotal = (dispatch) => {
  if (userHasReportPermissions([ReportName.SUPPLY_DEMAND])
    || validateRoleConfig(['management', 'viewReports'])) {
    dispatch({ type: 'SUPPLY_DEMAND_REPORT_GRAND_TOTAL_LOADING' });
    return RestAPI.get('/supplydemand/api/grandtotal')
      .then(
        (data) => {
          dispatch({ type: 'SUPPLY_DEMAND_REPORT_GRAND_TOTAL_LOADED', data });
          return true;
        }
      )
      .catch(
        (data) => {
          putError(dispatch, data);
          dispatch({ type: 'SUPPLY_DEMAND_GRAND_TOTAL_LOADED_FAILED' });
          return true;
        },
      );
  }
  return true;
};

export const addNewTotal = (dispatch, dataSD) => {
  dispatch({ type: 'SUPPLY_DEMAND_ADD_NEW_TOTAL_STARTING' });
  return RestAPI.post('/supplydemand/api/newtotal', dataSD)
    .then((data) => {
      dispatch({ type: 'SUPPLY_DEMAND_NEW_TOTAL_CREATED', data });
      return true;
    },
    )
    .catch(
      (data) => {
        putError(dispatch, data);
        dispatch({ type: 'SUPPLY_DEMAND_NEW_TOTAL_CREATED_FAILED' });
        return true;
      },
    );
};

export const loadAllTotal = (dispatch) => {
  if (userHasReportPermissions([ReportName.SUPPLY_DEMAND])
    || validateRoleConfig(['management', 'viewReports'])) {
    dispatch({ type: 'SUPPLY_DEMAND_ALL_TOTALS_LOADING' });
    return RestAPI.get('/supplydemand/api/alltotals')
      .then(
        (data) => {
          dispatch({ type: 'SUPPLY_DEMAND_ALL_TOTALS_LOADED', data });
          return true;
        },
      )
      .catch(
        (data) => {
          putError(dispatch, data);
          dispatch({ type: 'SUPPLY_DEMAND_ALL_TOTAL_LOADED_FAILED' });
          return true;
        },
      );
  }
  return true;
};

export const updateName = (dispatch, id, value) => {
  dispatch({ type: 'SUPPLY_DEMAND_TOTAL_NAME_UPDATING' });
  return RestAPI.patch(`/supplydemand/api/total/update/${id}`, value)
    .then(
      () => {
        dispatch({ type: 'SUPPLY_DEMAND_TOTAL_NAME_UPDATED', id, value });
        return true;
      }
    )
    .catch(
      (data) => {
        putError(dispatch, data);
        dispatch({ type: 'SUPPLY_DEMAND_TOTAL_NAME_UPDATED_FAILED' });
        return true;
      },
    );
};

export const deleteTotal = (dispatch, id) => {
  if (id == null) {
    dispatch({ type: 'SUPPLY_DEMAND_TOTAL_DELETED', id });
  } else {
    dispatch({ type: 'SUPPLY_DEMAND_TOTAL_DELETE_STARTED' });
    RestAPI.delete(`/supplydemand/api/total/delete/${id}`)
      .then(
        (data) => {
          dispatch({ type: 'SUPPLY_DEMAND_TOTAL_DELETED', data });
          return true;
        },
      ).catch(
        (data) => {
          putError(dispatch, data);
          dispatch({ type: 'SUPPLY_DEMAND_TOTAL_DELETE_FAILED' });
          return true;
        },
      );
  }
};

export const totalIndexUpdate = (dispatch, id, dataSDTotas) => {
  dispatch({ type: 'SUPPLY_DEMAND_TOTAL_INDEX_UPDATING' });
  return RestAPI.patch(`/supplydemand/api/total/update/index/${id}`, dataSDTotas)
    .then(
      (data) => {
        dispatch({ type: 'SUPPLY_DEMAND_TOTAL_INDEX_UPDATED', data });
        return true;
      }
    )
    .catch(
      (data) => {
        putError(dispatch, data);
        dispatch({ type: 'SUPPLY_DEMAND_TOTAL_INDEX_UPDATE_FAILED' });
        return true;
      },
    );
};
